import * as React from 'react';
import * as _ from 'lodash';
import useRestService from '../shared/rest-service';
import { PaginatedRevisionsContext } from '../shared/paginated-revisions';
import useTimedAlerts from './useTimedAlerts';

const usePaginatedRevisions = () => {
  const { results, dispatch } = React.useContext(PaginatedRevisionsContext);
  const { errorMessage, setErrorMessage, successMessage, setSuccessMessage } = useTimedAlerts();
  const { getAuth } = useRestService();

  /**
   * Gets a list of global revisions
   * @param documentId - the snippetId of the document to get the revisions for
   * @param currentPage - The page number for the revisions list
   * @param pageSize - The size of the revisions list
   */
  const getGlobalRevisions = (documentId: number, currentPage: number, pageSize: number) => {
    dispatch({ loading: true });
    return getAuth(
      `api/help-documents/${documentId}/revisions?Page=${currentPage}&PageSize=${pageSize}&Sort=revision&SortDirection=desc`
    )
      .then(response => {
        dispatch({
          currentPage,
          pageSize,
          results: response.results,
          totalResults: response.totalItems,
          loading: false
        });
      })
      .catch(err => {
        dispatch({ loading: false });
        setErrorMessage(err.responseJSON.message);
      });
  };

  /**
   * Gets a list of all client document revisions
   * @param clientId - Unique identifier of the client
   * @param documentId - the snippetId of the document to get the revisions for
   * @param currentPage - The page number for the revisions list
   * @param pageSize - The size of the revisions list
   */
  const getClientRevisions = (clientId: number, documentId: number, currentPage: number, pageSize: number) => {
    dispatch({ loading: true });
    return getAuth(
      `api/clients/${clientId}/help-documents/${documentId}/revisions?Page=${currentPage}&PageSize=${pageSize}&Sort=revision&SortDirection=desc`
    )
      .then(response => {
        dispatch({
          currentPage,
          pageSize,
          results: response.results,
          totalResults: response.totalItems,
          loading: false
        });
      })
      .catch(err => {
        dispatch({ loading: false });
        setErrorMessage(err.responseJSON.message);
      });
  };

  /**
   * Updates which revision is published in context
   * @param snippetId Identifier for a revision for a help document
   */
  const updatePublishedRevision = (snippetId: number) => {
    const revisions = _.cloneDeep(results);

    const previousActiveIndex = revisions.findIndex(revision => revision.isActive);
    if (previousActiveIndex > -1) {
      revisions[previousActiveIndex].isActive = false;
    }

    const nextActiveIndex = revisions.findIndex(revisions => revisions.snippetId === snippetId);
    if (nextActiveIndex > -1) {
      revisions[nextActiveIndex].isActive = true;
    }

    dispatch({ results: revisions });
  };

  return {
    getGlobalRevisions,
    updatePublishedRevision,
    errorMessage,
    setErrorMessage,
    successMessage,
    setSuccessMessage,
    getClientRevisions
  };
};

export default usePaginatedRevisions;
